import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { dealerLogin } from "../../api/dealerAuthApi";
import { DealerAuthProvider, useDealerAuth } from "../../auth/DealerAuthProvider";
import DealerPortalLocaleControls from "../../components/dealer/DealerPortalLocaleControls";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import { AUTOMAP_DEALER_DEMO_CREDENTIALS } from "../../constants/automapDemoPortalCredentials";
import { AUTOMAP_DEALER_PORTAL_I18N_MANIFEST } from "../../constants/automapDealerPortalI18n";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { IntlProvider } from "../../intl";

function DealerLoginForm() {
  const navigate = useNavigate();
  const { isAuthenticated, refresh } = useDealerAuth();
  const { t, translateError } = useDealerPortalIntl();
  const showDevHint = process.env.NODE_ENV === "development";
  const [email, setEmail] = useState(showDevHint ? AUTOMAP_DEALER_DEMO_CREDENTIALS.email : "");
  const [password, setPassword] = useState(showDevHint ? AUTOMAP_DEALER_DEMO_CREDENTIALS.password : "");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={automapDealerPortalPath("dashboard")} replace />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSubmitting(true);
    try {
      await dealerLogin(email, password);
      await refresh();
      navigate(automapDealerPortalPath("dashboard"));
    } catch (error) {
      setErrorMessage(translateError(error.message, "generic") || t("login.errors.signInFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="midpoint-app auth-page account-area-app">
      <section className="panel-card auth-card">
        <div className="dealer-login-locale">
          <DealerPortalLocaleControls />
        </div>
        <p className="eyebrow">{t("login.eyebrow")}</p>
        <h1>{t("login.title")}</h1>
        <p className="muted">{t("login.subtitle")}</p>
        <form onSubmit={handleSubmit}>
          <label className="admin-users-field">
            <span>{t("login.email")}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
            />
          </label>
          <label className="admin-users-field">
            <span>{t("login.password")}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </label>
          {errorMessage ? <p className="admin-data-health-error">{errorMessage}</p> : null}
          {showDevHint ? (
            <p className="muted" style={{ fontSize: "0.9rem" }}>
              Dev login: {AUTOMAP_DEALER_DEMO_CREDENTIALS.email} / {AUTOMAP_DEALER_DEMO_CREDENTIALS.password}
            </p>
          ) : null}
          <button type="submit" className="midpoint-share-button" disabled={submitting}>
            {submitting ? t("login.submitting") : t("login.submit")}
          </button>
        </form>
        <p className="muted">
          <Link to="/v/automap">{t("login.backToAutoMapa")}</Link>
        </p>
      </section>
    </div>
  );
}

export default function DealerLoginPage() {
  return (
    <IntlProvider surface="clientView" manifest={AUTOMAP_DEALER_PORTAL_I18N_MANIFEST}>
      <DealerAuthProvider>
        <DealerLoginForm />
      </DealerAuthProvider>
    </IntlProvider>
  );
}
